/**
 * Notification Service
 * Handles browser notifications for task reminders
 * 
 * Phase 1: Browser Notifications (simplest approach)
 * - Works when the app tab is open
 * - Uses the Web Notifications API
 */

import logger from '@/utils/logger';

// Check if browser supports notifications
export const isNotificationSupported = () => {
  return 'Notification' in window;
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission; // 'default', 'granted', or 'denied'
};

// Request permission to show notifications
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    logger.warn('[Notifications] Browser does not support notifications');
    return 'unsupported';
  }

  // Already granted
  if (Notification.permission === 'granted') {
    return 'granted';
  }

  // Already denied - user must manually enable in browser settings
  if (Notification.permission === 'denied') {
    logger.warn('[Notifications] User has denied notification permission');
    return 'denied';
  }

  // Ask for permission
  try {
    const permission = await Notification.requestPermission();
    logger.debug('[Notifications] Permission result:', permission);
    return permission;
  } catch (error) {
    logger.error('[Notifications] Error requesting permission:', error);
    return 'error';
  }
};

// Show a browser notification
export const showNotification = (title, options = {}) => {
  if (!isNotificationSupported()) {
    logger.warn('[Notifications] Browser does not support notifications');
    return null;
  }

  if (Notification.permission !== 'granted') {
    logger.warn('[Notifications] Permission not granted');
    return null;
  }

  const defaultOptions = {
    icon: '/favicon.ico', // FinList icon
    badge: '/favicon.ico',
    tag: options.tag || 'finlist-reminder',
    requireInteraction: false, // Auto-dismiss after a few seconds
    silent: false,
    ...options
  };

  try {
    const notification = new Notification(title, defaultOptions);

    // Handle click - focus the app
    notification.onclick = () => {
      window.focus();
      notification.close();
      
      // If there's a custom click handler, call it
      if (options.onClick) {
        options.onClick();
      }
    };

    // Auto-close after 10 seconds if not closed
    setTimeout(() => {
      notification.close();
    }, 10000);

    return notification;
  } catch (error) {
    logger.error('[Notifications] Error showing notification:', error);
    return null;
  }
};

// Show a task reminder notification
export const showTaskReminder = (task) => {
  const title = '⏰ Task Reminder';
  const body = task.title;
  
  // Add amount info for financial tasks
  let extendedBody = body;
  if (task.isFinancialTask && task.estimatedAmount) {
    const currency = task.currency === 'GBP' ? '£' : task.currency === 'NGN' ? '₦' : '$';
    extendedBody += ` (${currency}${task.estimatedAmount})`;
  }

  return showNotification(title, {
    body: extendedBody,
    tag: `task-reminder-${task.id}`, // Unique tag prevents duplicate notifications
    data: { taskId: task.id, type: 'task-reminder' }
  });
};

// Show a due date warning notification
export const showDueDateWarning = (task) => {
  const title = '📅 Task Due Soon';
  
  return showNotification(title, {
    body: `"${task.title}" is due today!`,
    tag: `task-due-${task.id}`,
    data: { taskId: task.id, type: 'due-warning' }
  });
};

// Show an overdue task notification
export const showOverdueNotification = (task) => {
  const title = '⚠️ Overdue Task';
  
  return showNotification(title, {
    body: `"${task.title}" is overdue!`,
    tag: `task-overdue-${task.id}`,
    data: { taskId: task.id, type: 'overdue' }
  });
};

// Storage key for tracking shown notifications (to avoid spam)
const SHOWN_NOTIFICATIONS_KEY = 'finlist_shown_notifications';

// Get list of already-shown notification IDs (for today)
export const getShownNotifications = () => {
  try {
    const stored = localStorage.getItem(SHOWN_NOTIFICATIONS_KEY);
    if (!stored) return { date: null, ids: [] };
    
    const data = JSON.parse(stored);
    const today = new Date().toDateString();
    
    // Reset if it's a new day
    if (data.date !== today) {
      return { date: today, ids: [] };
    }
    
    return data;
  } catch {
    return { date: new Date().toDateString(), ids: [] };
  }
};

// Mark a notification as shown (for today)
export const markNotificationShown = (notificationId) => {
  const data = getShownNotifications();
  const today = new Date().toDateString();
  
  const newData = {
    date: today,
    ids: [...new Set([...data.ids, notificationId])]
  };
  
  localStorage.setItem(SHOWN_NOTIFICATIONS_KEY, JSON.stringify(newData));
};

// Check if a notification was already shown today
export const wasNotificationShown = (notificationId) => {
  const data = getShownNotifications();
  return data.ids.includes(notificationId);
};

// Clear shown notifications (useful for testing)
export const clearShownNotifications = () => {
  localStorage.removeItem(SHOWN_NOTIFICATIONS_KEY);
};

export default {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showNotification,
  showTaskReminder,
  showDueDateWarning,
  showOverdueNotification,
  getShownNotifications,
  markNotificationShown,
  wasNotificationShown,
  clearShownNotifications
};
